import React from "react"
import ArticleMeta from "../../components/article-meta"
import CommentsForm from "../../components/comments-form"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import { Articles } from "../../data/articles"

const article = Articles.getBySlug("designing-a-workplace")

export default () => (
  <Layout>
    <SEO
      title={article.title}
      keywords={[article.title, ...article.categories]}
    />
    <div id="section">
      <div className="main-article">
        <ArticleMeta article={article} />

        <div className="article-content">
          <img
            src={article.image}
            className="attachment-wide-thumbnail"
            alt={article.title}
          />
          <p>
            Most of us would acknowledge that good design has a powerful
            influence on how people think and behave. As today’s companies wake
            up to the value of workers who are truly engaged in their work — a
            clear case of trying to encourage certain ways of thinking and
            behaving – they should probably be paying far more attention to
            place design. Few things are more instrumental in boosting — or
            diminishing — levels of employee engagement.
          </p>
          <p>
            Environmental psychologists are the design mavens of the scientific
            world. We’re the folks who concentrate our attention on how sensory
            experiences, psychosocial factors such as needing a territory, and
            basic psychological drives like having control over our physical
            environment, interact with personality and national culture to
            influence how attitudes and actions are affected by being in one
            space or another. Several dozen of us, working around the world, are
            moving what scientists have learned about how humans experience
            space from the dark corners of academia into the light of design
            practice, where it can make people’s lives better.
          </p>
          <p>
            One of the things that environmental psychologists focus on is how
            design affects mood. Via a chain of psychological chain reactions,
            mood influences worker engagement; more positive moods link to
            higher levels of engagement. Designing for engagement is designing
            to make those positive moods more likely.
          </p>
          <p>
            Workplaces where employees are engaged communicate to the people who
            work there — and anyone else in them — that their employer values
            them and the contribution that they make. The psychological lift
            that comes from feeling respected enhances engagement. It’s easy to
            publish mission and vision statements that go on, at length, about
            how much employees are prized by the people issuing their paychecks;
            so these proclamations are largely ignored by workers. Employees
            spend a lot of time “reading” the messages sent by the environments
            in which they’re asked to work. Physical workplaces cost money —
            think about all the times you’ve heard the expression “putting your
            money where your mouth is” — so the messages they send carry a lot
            more weight in workers’ minds that easy to generate verbal
            platitudes.
          </p>
          <p>
            A place indicates respect for employees when it supports them as
            they do their jobs. Workers want to do their work well. Workplace
            performance has a big influence on self-identity; not many of us
            want to live out our life as the employee who just doesn’t work out.
          </p>
          <p>
            Generally, designers talk to the employees who will work in a space
            about their jobs and how a workplace being designed can optimize
            their performance. Often, the spaces that are built out don’t align
            with those conversations. A workplace gets designed that looks good,
            looks like the one the CEO of the client firm just saw in a glossy
            magazine spread, or looks like it may accomplish some ill-defined
            objective, such as increasing collaboration.
          </p>
          <p>
            Workspaces should be designed so that each worker can do well
            whatever they do that adds the most value to their employer’s bottom
            line. That may be writing advertising copy or assessing financial
            plans or developing strategic objectives or something else entirely.
          </p>
          <p>
            Most workers need to be able to concentrate on the tasks at hand,
            and that’s difficult in a field of cubicles or in a sea of faces
            when the cubicles are removed and all employees are asked to sit at
            long tables. And those open spaces aren’t spurring useful
            communication. Research consistently shows that constructive, work
            related collaboration doesn’t increase when work environments are
            made more open.
          </p>
          <p>
            A range of workspaces can be provided for employees at a central
            office, and providing a smorgasbord of work locations is better than
            asking people to work in chaos, but people forget things, things
            that matter, every time they are asked to pack up what they’re
            working on and move to another place in their employer’s offices
            where they might be able to concentrate. It doesn’t take any sort of
            special training to know that thoughtful work may be thoughtlessly
            executed in spaces where there’s continuous action. Plus, management
            is assessed in part on the physical environments provided and
            workers aren’t too happy with those in charge when their workplaces
            just don’t work for them.
          </p>

          <CommentsForm title={article.title} />
        </div>
      </div>
    </div>
  </Layout>
)
